import { z } from "zod";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const HeatmapSchema = z.object({
  xLabels: z.array(z.string()),
  yLabels: z.array(z.string()),
  cells: z.array(
    z.array(
      z.object({
        value: z.number(),
        label: z.string().optional(),
      }),
    ),
  ),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
});

type HeatmapData = z.infer<typeof HeatmapSchema>;

/* ────────────────────── Color Helper ────────────────────── */

function cellColor(value: number, minValue: number, maxValue: number): string {
  if (maxValue === minValue) {
    return "hsl(210, 70%, 70%)";
  }
  const ratio = (value - minValue) / (maxValue - minValue);
  // hsl(210, 70%, 95%) → hsl(210, 70%, 30%)
  const lightness = 95 - ratio * 65;
  return `hsl(210, 70%, ${lightness.toFixed(1)}%)`;
}

function textColor(value: number, minValue: number, maxValue: number): string {
  if (maxValue === minValue) return "text-text-primary";
  const ratio = (value - minValue) / (maxValue - minValue);
  return ratio > 0.55 ? "text-white" : "text-text-primary";
}

/* ────────────────────── Component ────────────────────── */

interface TooltipState {
  row: number;
  col: number;
  x: number;
  y: number;
}

export function Heatmap({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<HeatmapData>) {
  const { xLabels, yLabels, cells, minValue, maxValue } = widget.data;
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const allValues = cells.flatMap((row) => row.map((c) => c.value));
  const min = minValue ?? (allValues.length > 0 ? Math.min(...allValues) : 0);
  const max = maxValue ?? (allValues.length > 0 ? Math.max(...allValues) : 1);

  const colCount = xLabels.length;

  return (
    <div
      className={cn("w-full overflow-x-auto", className)}
      onClick={
        widget.deepLink && onDeepLink
          ? () => onDeepLink(widget.deepLink!)
          : undefined
      }
    >
      <div className="relative inline-block min-w-full">
        {/* Header row: X labels */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `80px repeat(${colCount}, minmax(48px, 1fr))`,
          }}
        >
          {/* Top-left corner */}
          <div className="h-7" />
          {xLabels.map((label, colIdx) => (
            <div
              key={colIdx}
              className="flex items-center justify-center px-1 pb-1"
            >
              <span
                className="block truncate text-center text-[10px] font-medium text-text-tertiary"
                title={label}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Data rows */}
        {yLabels.map((yLabel, rowIdx) => {
          const row = cells[rowIdx] ?? [];
          return (
            <div
              key={rowIdx}
              className="grid"
              style={{
                gridTemplateColumns: `80px repeat(${colCount}, minmax(48px, 1fr))`,
              }}
            >
              {/* Y label */}
              <div className="flex items-center pr-2">
                <span
                  className="block truncate text-[10px] font-medium text-text-tertiary"
                  title={yLabel}
                >
                  {yLabel}
                </span>
              </div>

              {/* Cells */}
              {row.map((cell, colIdx) => {
                const bg = cellColor(cell.value, min, max);
                const tc = textColor(cell.value, min, max);
                const isHovered =
                  tooltip?.row === rowIdx && tooltip?.col === colIdx;

                return (
                  <div
                    key={colIdx}
                    className={cn(
                      "relative m-0.5 flex cursor-default items-center justify-center rounded-sm transition-opacity",
                      isHovered && "ring-1 ring-accent/60",
                    )}
                    style={{ height: 36, backgroundColor: bg }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const parentRect = e.currentTarget
                        .closest(".relative")
                        ?.getBoundingClientRect();
                      setTooltip({
                        row: rowIdx,
                        col: colIdx,
                        x: rect.left - (parentRect?.left ?? 0) + rect.width / 2,
                        y: rect.top - (parentRect?.top ?? 0) - 4,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <span
                      className={cn(
                        "text-[10px] font-semibold tabular-nums",
                        tc,
                      )}
                    >
                      {cell.label ?? cell.value.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Tooltip */}
        {tooltip !== null &&
          (() => {
            const cell = cells[tooltip.row]?.[tooltip.col];
            if (!cell) return null;
            return (
              <div
                className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border border-stroke-subtle bg-surface-raised px-2 py-1 text-[11px] shadow-md"
                style={{ left: tooltip.x, top: tooltip.y }}
              >
                <span className="font-semibold">{xLabels[tooltip.col]}</span>
                {" × "}
                <span className="font-semibold">{yLabels[tooltip.row]}</span>
                {": "}
                <span className="tabular-nums">
                  {cell.label ?? cell.value.toLocaleString()}
                </span>
              </div>
            );
          })()}
      </div>

      {/* Color scale legend */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] text-text-tertiary">낮음</span>
        <div
          className="h-2 flex-1 rounded-full"
          style={{
            background:
              "linear-gradient(to right, hsl(210,70%,95%), hsl(210,70%,30%))",
          }}
        />
        <span className="text-[10px] text-text-tertiary">높음</span>
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function HeatmapSkeleton({ className }: { className?: string }) {
  const rows = 4;
  const cols = 5;
  return (
    <div className={cn("w-full space-y-1", className)}>
      {/* Header */}
      <div className="flex gap-1 pl-20">
        {Array.from({ length: cols }, (_, i) => (
          <Skeleton
            key={i}
            className="flex-1 h-5 rounded"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }, (_, rowIdx) => (
        <div key={rowIdx} className="flex items-center gap-1">
          <Skeleton
            className="h-9 w-20 rounded"
            style={{ animationDelay: `${rowIdx * 80}ms` }}
          />
          {Array.from({ length: cols }, (_, colIdx) => (
            <Skeleton
              key={colIdx}
              className="flex-1 h-9 rounded-sm"
              style={{ animationDelay: `${(rowIdx * cols + colIdx) * 40}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const heatmapWidget = defineWidget<HeatmapData>({
  type: WIDGET_TYPES.HEATMAP,
  label: "히트맵 매트릭스",
  schema: HeatmapSchema,
  component: Heatmap,
  skeleton: HeatmapSkeleton,
  defaultLayout: "full",
  maxHeight: 400,
  aiHint: "키워드×채널 매트릭스 히트맵 (노출수, 클릭수 등 2D 크로스 분석)",
});

registerWidget(heatmapWidget);
